import React, { useEffect, useRef, useState } from 'react'
import { isApp } from '../utils/platform'
import { dataURLtoFile } from '../utils/fileUtils'
import LoadingSpinner from './LoadingSpinner'

/**
 * 위치 기반 사진 추천 타일
 *
 * 리뷰 작성 시 장소 좌표를 Flutter 브리지로 보내, 갤러리에서 그 근처에서
 * 촬영된 사진(최대 20장)을 추천 타일로 보여준다. 사진 추가(+) 버튼과 같은
 * 줄에 같은 크기로 이어지도록 타일들만 렌더링한다 (부모가 가로 스크롤 담당).
 * 탭하면 업로드용 이미지를 받아 onPickPhoto로 전달.
 *
 * 브리지(getPhotosNearLocation)가 없는 구버전 앱·웹 환경·권한 거부 시에는
 * 아무것도 렌더링하지 않는다 (안전한 폴백).
 */

interface NearbyPhoto {
  id: string
  thumbnail: string
  takenAt?: string
  distanceM?: number
}

interface NearbyPhotoSuggestionsProps {
  lat: number | null | undefined
  lng: number | null | undefined
  /** @param takenAt 사진 촬영일(ISO). 방문일자 기본값으로 쓴다. 없을 수 있다. */
  onPickPhoto: (file: File, preview: string, takenAt?: string) => void
}

// 실내 GPS 오차를 감안한 반경.
// 50m로 좁혀 뒀더니 같은 자리에서 찍은 사진인데도 일부만 추천에 잡혔다.
// (실내는 신호가 약해 사진마다 기록되는 좌표가 수십 m씩 흩어진다)
// 옆 가게 혼입을 막으면서 누락은 줄이는 선으로 80m를 쓴다.
const RADIUS_M = 80
const LIMIT = 20

/** 네이티브 브리지 주입을 기다리는 최대 시간 */
const BRIDGE_WAIT_MS = 5000
/** 브리지 준비 여부를 확인하는 간격 */
const BRIDGE_POLL_MS = 100

const NearbyPhotoSuggestions: React.FC<NearbyPhotoSuggestionsProps> = ({
  lat,
  lng,
  onPickPhoto,
}) => {
  const [photos, setPhotos] = useState<NearbyPhoto[]>([])
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const requestedRef = useRef(false)
  // 콜백이 stale closure를 잡지 않도록 ref로 유지
  const onPickPhotoRef = useRef(onPickPhoto)
  onPickPhotoRef.current = onPickPhoto

  useEffect(() => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return
    if (!isApp()) return
    if (requestedRef.current) return

    let cancelled = false
    let timer: number | null = null
    let boundBridge: any = null

    const request = (bridge: any) => {
      requestedRef.current = true
      boundBridge = bridge

      bridge.onNearbyPhotos = (data: {
        status?: string
        photos?: NearbyPhoto[]
        lat?: number
        lng?: number
      }) => {
        // 다른 장소(이전 화면)의 스캔 결과가 섞이지 않도록 요청 좌표와 대조
        if (
          (typeof data?.lat === 'number' && Math.abs(data.lat - lat) > 1e-6) ||
          (typeof data?.lng === 'number' && Math.abs(data.lng - lng) > 1e-6)
        ) {
          return
        }
        if (data?.status === 'ok' && Array.isArray(data.photos)) {
          setPhotos(data.photos.filter((p) => p?.id && p?.thumbnail))
        }
        // permission_denied / error → 타일 미노출 (조용한 폴백)
      }

      bridge.getPhotosNearLocation({ lat, lng, radiusM: RADIUS_M, limit: LIMIT })
    }

    /**
     * 브리지가 준비될 때까지 기다렸다가 요청한다.
     *
     * 네이티브 핸들러는 페이지 로드가 끝난 뒤 하나씩 순서대로 주입된다.
     * 갤러리 핸들러가 window.FlutterBridge 객체를 먼저 만들기 때문에,
     * "객체는 있는데 getPhotosNearLocation은 아직 없는" 순간이 존재한다.
     * 예전에는 그때 조용히 빠져나갔고, 의존성이 좌표뿐이라 다시 시도하지 않아
     * 그 화면에서는 추천이 영영 뜨지 않았다.
     * (앱을 켜자마자 기록 작성 화면으로 바로 들어간 경우에 걸린다)
     */
    const tryRequest = () => {
      if (cancelled) return
      const bridge = (window as any).FlutterBridge
      if (bridge && typeof bridge.getPhotosNearLocation === 'function') {
        if (timer !== null) {
          window.clearInterval(timer)
          timer = null
        }
        request(bridge)
      }
    }

    tryRequest()

    if (!requestedRef.current) {
      // 아직이면 잠깐 기다린다. 주입은 페이지 로드 직후 끝나므로 길게 볼 필요는 없다.
      const startedAt = Date.now()
      timer = window.setInterval(() => {
        if (cancelled || Date.now() - startedAt > BRIDGE_WAIT_MS) {
          if (timer !== null) {
            window.clearInterval(timer)
            timer = null
          }
          return
        }
        tryRequest()
      }, BRIDGE_POLL_MS)
    }

    return () => {
      cancelled = true
      if (timer !== null) {
        window.clearInterval(timer)
      }
      if (boundBridge) {
        delete boundBridge.onNearbyPhotos
        delete boundBridge.onNearbyPhotoOriginal
      }
    }
  }, [lat, lng])

  const handlePick = (photo: NearbyPhoto) => {
    if (loadingId) return
    const bridge = (window as any).FlutterBridge
    if (!bridge || typeof bridge.getNearbyPhotoOriginal !== 'function') return

    setLoadingId(photo.id)
    bridge.onNearbyPhotoOriginal = (data: {
      id?: string
      name?: string
      data?: string
    }) => {
      setLoadingId(null)
      if (!data?.data) return
      try {
        const file = dataURLtoFile(data.data, data.name || 'nearby-photo.jpg')
        // 탭한 사진의 촬영일을 같이 넘긴다. 목록을 받을 때부터 갖고 있었는데
        // 여기서 버려지고 있었다(앱이 photo_manager 로 읽어 보내준 값).
        onPickPhotoRef.current(file, data.data, photo.takenAt)
      } catch (e) {
        console.error('추천 사진 변환 실패:', e)
      }
    }
    bridge.getNearbyPhotoOriginal({ id: photo.id })
  }

  if (photos.length === 0) return null

  return (
    <>
      {photos.map((photo) => (
        <button
          key={photo.id}
          type="button"
          onClick={() => handlePick(photo)}
          disabled={loadingId !== null}
          className={`relative flex-shrink-0 w-92px h-116px rounded-16px overflow-hidden bg-472410-5% cursor-pointer
            ${loadingId === photo.id ? 'opacity-100' : 'opacity-50 hover:opacity-70'}`}
        >
          <img
            src={photo.thumbnail}
            alt="이 장소 근처에서 찍은 사진"
            className="w-full h-full object-cover"
          />
          {loadingId === photo.id && (
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
              <LoadingSpinner size={28} borderWidth={2} />
            </div>
          )}
        </button>
      ))}
    </>
  )
}

export default NearbyPhotoSuggestions
